import React, {
  useMemo,
} from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@mui/material/styles";

import BasePaper from "components/Base/BasePaper";
import {
  RectangularTextButton,
} from "components/Base/Buttons";

export interface ExpectedPersonListItem {
  id: number;
  primary: string;
  secondary?: string;
}

interface Props {
  addedPeople: ExpectedPersonListItem[];
  expectedPeople: ExpectedPersonListItem[];

  attendedPersonIds: number[];
  selectedPersonId?: number | null;

  loading?: boolean;
  attendanceLoadingPersonId?: number | null;
  addingDatabasePeople?: boolean;

  externalAccessToPeopleDatabase?: boolean;

  onAddNewPerson: () => void;
  onAddDatabasePeople: () => void;

  onAttendanceChange: (
    person: ExpectedPersonListItem,
    attended: boolean
  ) => Promise<void> | void;

  onSelectPerson: (
    person: ExpectedPersonListItem
  ) => Promise<void> | void;
}

interface PeopleSectionProps {
  title: string;
  people: ExpectedPersonListItem[];

  attendedPersonIds: number[];
  selectedPersonId?: number | null;
  attendanceLoadingPersonId?: number | null;

  onAttendanceChange: (
    person: ExpectedPersonListItem,
    attended: boolean
  ) => Promise<void> | void;

  onSelectPerson: (
    person: ExpectedPersonListItem
  ) => Promise<void> | void;
}

const BlankPersonRow: React.FC = () => {
  return (
    <Box
      aria-hidden="true"
      sx={{
        minHeight: {
          xs: 58,
          sm: 62,
        },
        width: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        backgroundColor:
          "background.default",
      }}
    />
  );
};

const PeopleSection: React.FC<
  PeopleSectionProps
> = ({
  title,
  people,
  attendedPersonIds,
  selectedPersonId = null,
  attendanceLoadingPersonId = null,
  onAttendanceChange,
  onSelectPerson,
}) => {
  const theme = useTheme();

  const sortedPeople = useMemo(
    () =>
      [...people].sort((a, b) =>
        a.primary.localeCompare(
          b.primary
        )
      ),
    [people]
  );

  return (
    <Box>
      <Box
        sx={{
          mb: 1,
          px: {
            xs: 1.25,
            sm: 1.5,
          },
          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            minWidth: 0,
            fontSize: {
              xs: "1rem",
              sm: "1.1rem",
            },
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            width: {
              xs: 42,
              sm: 48,
            },
            flexShrink: 0,
            textAlign: "center",
            color: "text.secondary",
            fontWeight: 600,
          }}
        >
          Attended
        </Typography>
      </Box>

      {sortedPeople.length === 0 ? (
        <BlankPersonRow />
      ) : (
        <List
          disablePadding
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {sortedPeople.map(
            (person) => {
              const personId =
                Number(person.id);

              const attended =
                attendedPersonIds.includes(
                  personId
                );

              const selected =
                selectedPersonId !==
                  null &&
                Number(
                  selectedPersonId
                ) === personId;

              const attendanceLoading =
                attendanceLoadingPersonId !==
                  null &&
                Number(
                  attendanceLoadingPersonId
                ) === personId;

              return (
                <ListItemButton
                  key={personId}
                  selected={selected}
                  onClick={() =>
                    onSelectPerson(
                      person
                    )
                  }
                  sx={{
                    minHeight: {
                      xs: 58,
                      sm: 62,
                    },

                    width: "100%",

                    px: {
                      xs: 1.25,
                      sm: 1.5,
                    },

                    py: {
                      xs: 0.75,
                      sm: 1,
                    },

                    border:
                      "1px solid",

                    borderColor:
                      selected
                        ? theme.palette
                            .primary.main
                        : theme.palette
                            .divider,

                    borderRadius: 1.5,
                    alignItems: "center",

                    transition:
                      "background-color 120ms ease, border-color 120ms ease",

                    "&.Mui-selected":
                      {
                        backgroundColor:
                          theme.palette
                            .action
                            .selected,
                      },

                    "&.Mui-selected:hover":
                      {
                        backgroundColor:
                          theme.palette
                            .action
                            .selected,
                      },
                  }}
                >
                  <ListItemText
                    primary={
                      person.primary
                    }
                    secondary={
                      person.secondary
                    }
                    sx={{
                      minWidth: 0,
                      pr: 1,
                    }}
                    slotProps={{
                      primary: {
                        sx: {
                          fontWeight:
                            selected
                              ? 600
                              : 500,

                          overflow:
                            "hidden",

                          textOverflow:
                            "ellipsis",

                          whiteSpace:
                            "nowrap",
                        },
                      },

                      secondary: {
                        sx: {
                          overflow:
                            "hidden",

                          textOverflow:
                            "ellipsis",

                          whiteSpace:
                            "nowrap",
                        },
                      },
                    }}
                  />

                  <Box
                    onClick={(event) =>
                      event.stopPropagation()
                    }
                    onKeyDown={(
                      event
                    ) =>
                      event.stopPropagation()
                    }
                    sx={{
                      ml: "auto",
                      flexShrink: 0,

                      width: {
                        xs: 42,
                        sm: 48,
                      },

                      display: "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                    }}
                  >
                    {attendanceLoading ? (
                      <CircularProgress
                        size={22}
                      />
                    ) : (
                      <Checkbox
                        checked={
                          attended
                        }
                        onChange={(
                          event
                        ) =>
                          onAttendanceChange(
                            person,
                            event.target
                              .checked
                          )
                        }
                        icon={
                          <CheckCircleIcon />
                        }
                        checkedIcon={
                          <CheckCircleIcon />
                        }
                        sx={{
                          p: 1,

                          color:
                            theme.palette
                              .action
                              .disabled,

                          "&.Mui-checked":
                            {
                              color:
                                theme
                                  .palette
                                  .success
                                  .main,
                            },
                        }}
                        inputProps={{
                          "aria-label": `Mark ${person.primary} as attended`,
                        }}
                      />
                    )}
                  </Box>
                </ListItemButton>
              );
            }
          )}
        </List>
      )}
    </Box>
  );
};

const ExpectedPeopleAttendance: React.FC<
  Props
> = ({
  addedPeople,
  expectedPeople,
  attendedPersonIds,
  selectedPersonId = null,
  loading = false,
  attendanceLoadingPersonId = null,
  addingDatabasePeople = false,
  externalAccessToPeopleDatabase = false,
  onAddNewPerson,
  onAddDatabasePeople,
  onAttendanceChange,
  onSelectPerson,
}) => {
  return (
    <BasePaper>
      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            sm: externalAccessToPeopleDatabase
              ? "repeat(2, minmax(0, 1fr))"
              : "1fr",
          },

          gap: {
            xs: 1,
            sm: 1.5,
          },

          mb: {
            xs: 2,
            sm: 3,
          },
        }}
      >
        <RectangularTextButton
          colour="highlighted"
          buttonSize="large"
          startIcon={
            <PersonAddAlt1Icon />
          }
          onClick={onAddNewPerson}
          sx={{
            minHeight: {
              xs: 50,
              sm: 58,
            },

            width: "100%",

            px: {
              xs: 1.5,
              sm: 2,
            },

            fontWeight: 600,
          }}
        >
          Add New Person
        </RectangularTextButton>

        {externalAccessToPeopleDatabase && (
          <RectangularTextButton
            colour="highlighted"
            buttonSize="large"
            startIcon={
              <PersonSearchIcon />
            }
            onClick={
              onAddDatabasePeople
            }
            disabled={
              loading ||
              addingDatabasePeople
            }
            sx={{
              minHeight: {
                xs: 50,
                sm: 58,
              },

              width: "100%",

              px: {
                xs: 1.5,
                sm: 2,
              },

              fontWeight: 600,
            }}
          >
            Add from Database
          </RectangularTextButton>
        )}
      </Box>

      {loading ? (
        <Box
          sx={{
            minHeight: 180,
            display: "flex",
            alignItems: "center",
            justifyContent:
              "center",
          }}
        >
          <CircularProgress
            size={28}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",

            gap: {
              xs: 2,
              sm: 2.5,
            },
          }}
        >
          <PeopleSection
            title="Attended People"
            people={addedPeople}
            attendedPersonIds={
              attendedPersonIds
            }
            selectedPersonId={
              selectedPersonId
            }
            attendanceLoadingPersonId={
              attendanceLoadingPersonId
            }
            onAttendanceChange={
              onAttendanceChange
            }
            onSelectPerson={
              onSelectPerson
            }
          />

          <Divider />

          <PeopleSection
            title="Expected People"
            people={expectedPeople}
            attendedPersonIds={
              attendedPersonIds
            }
            selectedPersonId={
              selectedPersonId
            }
            attendanceLoadingPersonId={
              attendanceLoadingPersonId
            }
            onAttendanceChange={
              onAttendanceChange
            }
            onSelectPerson={
              onSelectPerson
            }
          />

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: "center",
            }}
          >
            {
              "Before recording anyone's personal information, make sure they understand what data you are collecting and why."
            }
          </Typography>
        </Box>
      )}
    </BasePaper>
  );
};

export default ExpectedPeopleAttendance;